import React, { ReactNode, useMemo, useState } from "react";
import styled from "styled-components";
import { LanguageNameSpaces, SignalDataWithKey } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import FlatList from "common/Lists/FlatList";
import { getNewLookTokens, NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { NEW_LOOK_PHONE_GUTTER } from "common/NewLook/layout.ts";
import {
  PhoneSectionHead,
  PhoneSortMenu,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import useExpandedTrade from "features/trades/NewLook/useExpandedTrade.ts";
import PhoneSignalCard, {
  PhoneSignalCardSkeleton,
} from "features/signals/NewLook/PhoneSignalCard.tsx";
import {
  PHONE_SIGNAL_SORTERS,
  PhoneSignalSortKey,
} from "features/signals/NewLook/phoneSignalCardFigures.ts";
import { PHONE_SIGNALS_SORT_ID } from "testIds";

const SKELETON_COUNT = 3;
const WRAPPER_STYLE = { padding: "0 0 8px" };
const SORT_LABELS: Record<
  PhoneSignalSortKey,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  recent: "phone.sort_recent",
  profit: "phone.sort_profit",
  potential: "phone.sort_potential",
  symbol: "phone.sort_symbol",
};

type PhoneListItem =
  | { kind: "head" }
  | { kind: "skeleton"; index: number }
  | { kind: "signal"; signal: SignalDataWithKey };

interface PhoneSignalsListProps {
  data: SignalDataWithKey[];
  loading: boolean;
  isOpenSignals?: boolean;
  renderTopContainer?: () => React.JSX.Element;
  emptyState: ReactNode;
  refreshing?: boolean;
  onRefresh?: () => void;
}

const PhoneSignalsList = ({
  data,
  loading,
  isOpenSignals,
  renderTopContainer,
  emptyState,
  refreshing,
  onRefresh,
}: PhoneSignalsListProps) => {
  const [sortKey, setSortKey] = useState<PhoneSignalSortKey>("recent");
  const { expandedTradeId: expandedId, toggleExpanded } = useExpandedTrade();
  const items = usePhoneItems(data, sortKey, loading);

  const renderContent = ({ item }: { item: PhoneListItem }) => {
    if (item.kind === "head")
      return (
        <ListHead
          isOpenSignals={isOpenSignals}
          sortKey={sortKey}
          onSort={setSortKey}
        />
      );
    if (item.kind === "skeleton") return <PhoneSignalCardSkeleton />;
    return (
      <PhoneSignalCard
        signal={item.signal}
        expanded={expandedId === item.signal.exchange_signal_id}
        onToggleExpanded={toggleExpanded}
      />
    );
  };

  return (
    <FlatList
      data={items}
      gap={0}
      loading={false}
      refreshing={refreshing}
      onRefresh={onRefresh}
      renderContent={renderContent}
      renderTopContainer={renderTopContainer}
      emptyState={<EmptyPanel>{emptyState}</EmptyPanel>}
      paddingHorizontal={0}
      wrapperStyle={WRAPPER_STYLE}
      hasActionButton={true}
    />
  );
};

export default PhoneSignalsList;

const ListHead = ({
  isOpenSignals,
  sortKey,
  onSort,
}: {
  isOpenSignals?: boolean;
  sortKey: PhoneSignalSortKey;
  onSort: (key: PhoneSignalSortKey) => void;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  return (
    <PhoneSectionHead
      title={t(
        isOpenSignals ? "phone.list_title_open" : "phone.list_title_closed"
      )}
      trailing={
        <div data-testid={PHONE_SIGNALS_SORT_ID}>
          <PhoneSortMenu
            options={(Object.keys(SORT_LABELS) as PhoneSignalSortKey[]).map(
              (key) => ({ key, label: t(SORT_LABELS[key]) })
            )}
            value={sortKey}
            onChange={onSort}
            label={t("phone.sort_label")}
          />
        </div>
      }
    />
  );
};

const usePhoneItems = (
  data: SignalDataWithKey[],
  sortKey: PhoneSignalSortKey,
  loading: boolean
) =>
  useMemo((): PhoneListItem[] => {
    if (loading && !data.length)
      return [
        { kind: "head" },
        ...Array.from({ length: SKELETON_COUNT }, (_, index) => ({
          kind: "skeleton" as const,
          index,
        })),
      ];
    if (!data.length) return [];
    return [
      { kind: "head" },
      ...[...data]
        .sort(PHONE_SIGNAL_SORTERS[sortKey])
        .map((signal) => ({ kind: "signal" as const, signal })),
    ];
  }, [data, sortKey, loading]);

const EmptyPanel = styled.div`
  margin: 12px ${NEW_LOOK_PHONE_GUTTER}px 0;
  padding: 8px 16px 20px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
`;
